import { useState } from 'react';
import { Badge, Button, InlineError, Input, Panel, Skeleton } from '../../components/ui';
import { BriefCopyLinkButton } from '../sharing/BriefCopyLinkButton';
import { briefPortalUrl } from '../sharing/shareLinks';
import { linkName, revealFor, sortedLinks, usageLine, type Reveal } from './briefTokens';
import { useBriefLinks, useMintBriefLink, useRevokeBriefLink } from './hooks';
import { briefLinkErrorMessage } from './queries';

/**
 * The links that open the brief without a Hub account.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Drop 140 built the public page, the token table and three functions to mint,
 * list and revoke. It built no door to any of them. On 14 September Kyu needed
 * a link for Alex and the only way to get one was a hand-written insert in the
 * Supabase SQL editor, which is exactly the console work he keeps out of his
 * week. The masthead's copy button, meanwhile, was handing out the Hub address
 * and sending its recipient to the supplier portal.
 *
 * ── The shape of it is decided by one database fact ────────────────────────
 *
 * list_brief_tokens returns the last six characters of a token and never the
 * token. A live token exists, readable, for exactly as long as the mint call's
 * response, and then never again. So this panel cannot be "here are your
 * links, click to copy". It is a list you can read and kill, plus one moment
 * of revelation when you make a new one.
 *
 * That moment is the only place a token is on screen, it is not stored, and a
 * reload does not bring it back. If that feels harsh, the alternative is a
 * permanent key to every brief ever written sitting in a query cache.
 */
export function BriefLinksPanel() {
  const links = useBriefLinks();
  const mint = useMintBriefLink();
  const revoke = useRevokeBriefLink();
  const [label, setLabel] = useState('');
  const [reveal, setReveal] = useState<Reveal | null>(null);
  const [error, setError] = useState('');

  const rows = sortedLinks(links.data ?? []);
  const now = new Date();

  // Nothing to draw for anybody who is not a super admin: the function returns
  // no rows to them, so this is empty rather than forbidden. The panel would
  // still be harmless, but a header over an empty box invites a support
  // question about a feature the reader cannot have.
  if (links.isPending) {
    return <Skeleton />;
  }
  if (links.isError && rows.length === 0) {
    return null;
  }

  function create(): void {
    const name = label.trim();
    if (name === '') {
      setError('Give the link a name, so you can tell it from the next one.');
      return;
    }
    setError('');
    mint.mutate(name, {
      onSuccess: (minted) => {
        setReveal(revealFor(minted));
        setLabel('');
      },
      onError: (cause) => setError(briefLinkErrorMessage(cause)),
    });
  }

  return (
    <Panel title="Brief links">
      <p className="brief-link-intro">
        These open the brief without signing in. Anybody holding one can read every brief, so revoke
        the ones nobody is using.
      </p>
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {/* The one moment a token is readable. Stays until dismissed, because
          the cost of losing it to a stray click is minting another permanent
          key, and the copy button has its own manual fallback for the browsers
          that refuse the clipboard. */}
      {reveal === null ? null : (
        <div className="brief-link-reveal" role="status">
          <p className="brief-link-reveal-title">{reveal.name} is ready</p>
          <Input
            label="Copy this link now"
            readOnly
            value={briefPortalUrl(window.location.origin, reveal.token)}
            onFocus={(event) => event.currentTarget.select()}
          />
          <p className="brief-link-reveal-note">
            This is the only time the link is shown. It is not stored anywhere you can read it back,
            so if you lose it you will have to make another. Anybody holding it can read every
            brief.
          </p>
          <div className="brief-link-reveal-row">
            <BriefCopyLinkButton token={reveal.token} label="Copy link" />
            <Button variant="secondary" size="sm" onClick={() => setReveal(null)}>
              Done
            </Button>
          </div>
        </div>
      )}

      <div className="brief-link-new">
        <Input
          label="Name the new link"
          placeholder="Alex's phone"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              create();
            }
          }}
        />
        <Button size="sm" disabled={mint.isPending} onClick={create}>
          {mint.isPending ? 'Making it...' : 'New link'}
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="brief-link-empty">
          No links yet. Make one and send it to whoever should read the brief without a Hub account.
        </p>
      ) : (
        <ul className="brief-link-list">
          {rows.map((row) => {
            const dead = row.revoked_at !== null;
            return (
              <li
                key={row.token_tail + row.created_at}
                className={`brief-link-row${dead ? ' brief-link-row--dead' : ''}`}
              >
                <div className="brief-link-row-main">
                  <p className="brief-link-row-name">{linkName(row)}</p>
                  <p className="brief-link-row-meta">
                    Ends {row.token_tail} · {usageLine(row, now)}
                  </p>
                </div>
                {dead ? (
                  <Badge tone="neutral">Revoked</Badge>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={revoke.isPending}
                    onClick={() => {
                      setError('');
                      revoke.mutate(row.token_tail, {
                        onError: (cause) => setError(briefLinkErrorMessage(cause)),
                      });
                    }}
                  >
                    Revoke
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
